import app from '../../app';
import {
    AuthConstants,
    OauthQueryConstants,
    UserJourneyTypes
} from './constants/auth-constants';
import { hasValue, stringToBoolean } from '../../general-helpers';
import {
    addQueryParams,
    getLocalStorageItem,
    getPageUrl,
    getParameterByName,
    getSessionStorageItem,
    removeParameterByName,
    setLocalStorageItem,
    setSessionStorageItem
} from '..';
import { IdentityBrokerConfig } from './constants/identity-broker-config';
import { QueryConstants } from '../query-constants';

export const getOauthReturnUrl = ({
    useEncode = true,
    noTrailingSlash = true,
    noUrlAnchor = true,
    includeWholeURL = false
} = {
    useEncode: true,
    noTrailingSlash: true,
    noUrlAnchor: true,
    includeWholeURL: false
}) => {
    let currentPageUrl = getPageUrl();

    if (!includeWholeURL) {
        if (noUrlAnchor) {
            currentPageUrl = currentPageUrl.split('#')[0];
        }

        if (noTrailingSlash) {
            currentPageUrl = currentPageUrl.replace(/\/+$/, '');
        }
    }

    return useEncode ?
        encodeURIComponent(currentPageUrl) :
        currentPageUrl;
};

export const setOauthSignInReturnUrl = () => {
    let redirectUri = getParameterByName(QueryConstants.REDIRECT_TO);

    if (!hasValue(redirectUri)) {
        redirectUri = getOauthReturnUrl({
            noUrlAnchor: false
        });
    }

    setSessionStorageItem(AuthConstants.OauthSignInReturnUrl, redirectUri);
};

export const setOauthFlowReturnUrl = () => {
    let returnUrl = getParameterByName(QueryConstants.REDIRECT_TO);

    if (!hasValue(returnUrl)) {
        returnUrl = getOauthReturnUrl({
            noUrlAnchor: false
        });
    }

    setLocalStorageItem(AuthConstants.OauthFlowReturnUrl, returnUrl);
};

export const setOauthSignOutReturnUrl = () => {
    let oauthSignOutReturnUrl = getParameterByName(QueryConstants.REDIRECT_TO);

    if (hasValue(oauthSignOutReturnUrl)) {
        setSessionStorageItem(AuthConstants.OauthSignOutReturnUrl, oauthSignOutReturnUrl);
        return;
    }

    if (window.location.href.includes('account/profile')) {
        oauthSignOutReturnUrl = encodeURIComponent(window.location.origin);
    } else {
        oauthSignOutReturnUrl = getOauthReturnUrl({
            noUrlAnchor: false
        });
    }

    setSessionStorageItem(AuthConstants.OauthSignOutReturnUrl, oauthSignOutReturnUrl);
};

export const getOauthSignInReturnUrl = (purgeValue = false, useDefault = true) => {
    let returnUri = getSessionStorageItem(AuthConstants.OauthSignInReturnUrl, purgeValue);

    if (!hasValue(returnUri) && useDefault) {
        returnUri = app.state.ssoConfig.oAuthRedirectSignIn;
    }

    return hasValue(returnUri) ? decodeURIComponent(returnUri) : undefined;
};

export const getRedirectUriPath = (purgeValue = false) => {
    const redirectUriPath = getLocalStorageItem(OauthQueryConstants.REDIRECT_URI_PATH, purgeValue);


    return hasValue(redirectUriPath) ? decodeURIComponent(redirectUriPath) : undefined;
};

export const getOauthFlowReturnUrl = () => {
    const returnUri = getLocalStorageItem(AuthConstants.OauthFlowReturnUrl, true);

    return hasValue(returnUri) ? decodeURIComponent(returnUri) : undefined;
};

export const getOauthSignOutReturnUrl = (purgeValue = false, useDefault = true) => {
    let returnUri = getSessionStorageItem(AuthConstants.OauthSignOutReturnUrl, purgeValue);

    if (!hasValue(returnUri) && useDefault) {
        returnUri = app.state.ssoConfig.oAuthRedirectSignOut;
    }

    return hasValue(returnUri) ? decodeURIComponent(returnUri) : undefined;
};

export const getOauthRedirectUri = (oauthRoute, includeWholeURL = false, userJourneyType = undefined) => {
    if (!hasValue(oauthRoute)) {
        return undefined;
    }

    return addQueryParams(`${IdentityBrokerConfig.oauthClientDomain}/${oauthRoute}`,
        [
            {
                key: OauthQueryConstants.CLIENT_ID,
                value: app.state.ssoConfig.userPoolWebClientId,
            },
            {
                key: OauthQueryConstants.REDIRECT_URI,
                value: getOauthReturnUrl({ includeWholeURL }),
            },
            {
                key: OauthQueryConstants.USER_JOURNEY_TYPE,
                value: userJourneyType,
            },
        ]);
};

export const buildCustomOauthState = (oauthRoute = undefined, userJourneyType = undefined, redirectUriPath = undefined) => {
    if (!hasValue(oauthRoute)) {
        return undefined;
    }

    return JSON.stringify({
        slug: oauthRoute,
        userJourneyType,
        redirectUriPath,
    });
};

export const isOauthSignOutFlow = () => {
    return hasValue(getOauthSignOutReturnUrl());
};

export const checkOauthFlow = (oauthQueryKey) => {
    if (!hasValue(oauthQueryKey)) {
        return undefined;
    }

    const oauthFlow = stringToBoolean(getParameterByName(oauthQueryKey));
    removeParameterByName(oauthQueryKey);

    return oauthFlow;
};

export const getOauthPathFromRoute = (oauthRoute = undefined) => {
    const { path } = hasValue(oauthRoute) ? oauthRoute : { path: undefined };

    return path;
};

export const getOauthUserJourneyFromRoute = (oauthRoute = undefined) => {
    const { userJourneyType } = hasValue(oauthRoute) ? oauthRoute : { userJourneyType: undefined };

    return userJourneyType;
};

export const parseUserJourneyType = (userJourney) => {
    if (!hasValue(userJourney)) {
        return UserJourneyTypes.Default;
    }

    const validUserJourneyIndex = Object.values(UserJourneyTypes).findIndex((uJType) => uJType.toLowerCase() === userJourney.toLowerCase());

    if (validUserJourneyIndex === -1) {
        return UserJourneyTypes.Default;
    }

    return Object.values(UserJourneyTypes)[validUserJourneyIndex];
};

export const getUserJourneyTypeIndex = (userJourney) => {
    if (!hasValue(userJourney)) {
        return -1;
    }

    return Object.values(UserJourneyTypes).findIndex((uJType) => uJType.toLowerCase() === userJourney.toLowerCase());
};
